import React, { useState, useEffect } from 'react';
import { 
    Alert, 
    Box, 
    IconButton, 
    Collapse,
    Typography,
    Chip
} from '@mui/material';
import { 
    Close,
    Info,
    Warning,
    CheckCircle,
    Error
} from '@mui/icons-material';
import { APIs } from '../utilities/APIs';

const ApplicantNotices = ({ page = 'all', maxNotices = 5 }) => {
    const [notices, setNotices] = useState([]);
    const [dismissedNotices, setDismissedNotices] = useState(new Set());
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchNotices();
    }, [page]);    const fetchNotices = async () => {
        try {
            const endpoint = page === 'all' ? APIs.notices.active : APIs.notices.forPage(page);
            console.log(`Fetching notices for page: ${page}, endpoint: ${endpoint}`);
            const response = await fetch(endpoint);
            const data = await response.json();
            console.log(`Notice response for page ${page}:`, data);
            
            if (data.status === 'success' && data.data) {
                // Limit the number of notices displayed
                const limitedNotices = data.data.slice(0, maxNotices);
                setNotices(limitedNotices);
                console.log(`Set ${limitedNotices.length} notices for page ${page}`);
            } else {
                console.log(`No notices found for page ${page}:`, data);
            }
        } catch (error) {
            console.error('Error fetching notices:', error);
        } finally {
            setLoading(false);
        }
    };

    const dismissNotice = (noticeId) => {
        setDismissedNotices(prev => new Set(prev).add(noticeId));
    };

    const getNoticeIcon = (type) => {
        switch (type?.toLowerCase()) {
            case 'warning':
                return <Warning />;
            case 'success':
                return <CheckCircle />;
            case 'error':
                return <Error />;
            case 'info':
            default:
                return <Info />;
        }
    };

    const getNoticeSeverity = (type) => {
        switch (type?.toLowerCase()) {
            case 'warning':
                return 'warning';
            case 'success':
                return 'success';
            case 'error':
                return 'error';
            case 'info':
            default:
                return 'info';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 3:
                return 'error';
            case 2:
                return 'warning';
            case 1:
            default:
                return 'primary';
        }
    };

    const visibleNotices = notices.filter(notice => !dismissedNotices.has(notice.id));

    if (loading || visibleNotices.length === 0) {
        return null;
    }

    return (
        <Box sx={{ mb: 2 }}>
            {visibleNotices.map((notice) => (
                <Collapse key={notice.id} in={true}>                    <Alert
                        severity={getNoticeSeverity(notice.noticeType || notice.type)}
                        icon={getNoticeIcon(notice.noticeType || notice.type)}
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => dismissNotice(notice.id)}
                            >
                                <Close fontSize="inherit" />
                            </IconButton>
                        }
                        sx={{ 
                            mb: 1,
                            '& .MuiAlert-message': {
                                width: '100%'
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                {notice.title}
                            </Typography>
                            {notice.priority > 1 && (
                                <Chip
                                    label={notice.priority === 3 ? 'High Priority' : 'Medium Priority'}
                                    size="small"
                                    color={getPriorityColor(notice.priority)}
                                    variant="outlined"
                                />
                            )}
                        </Box>
                        <Typography variant="body2">
                            {notice.content}
                        </Typography>
                        {notice.createdAt && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
                                Posted: {new Date(notice.createdAt).toLocaleDateString()}
                            </Typography>
                        )}
                    </Alert>
                </Collapse>
            ))}
        </Box>
    );
};

export default ApplicantNotices;
